import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { extractionService } from '../../services/extractionService';
import { processDocument } from '../../services/api';
import type { Document } from '../../types/document';

interface Props {
  document: Document;
  onActionComplete?: () => void;
  /** Tenant extraction policy ('manual' | 'auto' | 'scheduled'). When 'manual',
   *  the "Lancer extraction" button is shown on 'uploaded' rows. */
  extractionPolicy?: string;
}

export function ExtractionActions({ document, onActionComplete, extractionPolicy }: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!document.id) return;

    setLoading(true);
    setError(null);
    try {
      await extractionService.cancelDocument(document.id);
      onActionComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'annulation');
      console.error('Cancel error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!document.id) return;

    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await extractionService.deleteDocument(document.id);
      onActionComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
      console.error('Delete error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!document.id) return;

    setLoading(true);
    setError(null);
    try {
      await extractionService.retryExtraction(document.id);
      onActionComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du réessai');
      console.error('Retry error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualExtract = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!document.id) return;
    setLoading(true);
    setError(null);
    try {
      await processDocument(document.id);
      toast.success('Extraction lancée');
      onActionComplete?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur lors du lancement';
      toast.error(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/extraction/documents/${document.id}`);
  };

  const renderActions = () => {
    const status = document.status;

    switch (status) {
      case 'uploaded':
        return (
          <div className="flex items-center gap-2">
            {extractionPolicy === 'manual' && (
              <button
                onClick={handleManualExtract}
                disabled={loading}
                className="px-1.5 py-1 text-base text-purple-600 hover:text-purple-800 disabled:opacity-50"
                title="Lancer extraction"
              >
                {loading ? '…' : '🤖'}
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-1.5 py-1 text-base text-red-600 hover:text-red-800 disabled:opacity-50"
              title="Supprimer"
            >
              🗑
            </button>
          </div>
        );

      case 'processing':
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-1.5 py-1 text-base text-blue-600 hover:text-blue-800 disabled:opacity-50"
              title="Stop"
            >
              ⏹
            </button>
          </div>
        );

      case 'extracted':
      case 'validated':
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={handleView}
              className="px-1.5 py-1 text-base text-blue-600 hover:text-blue-800"
              title="Voir"
            >
              👁
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-1.5 py-1 text-base text-red-600 hover:text-red-800 disabled:opacity-50"
              title="Supprimer"
            >
              🗑
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={handleRetry}
              disabled={loading}
              className="px-1.5 py-1 text-base text-blue-600 hover:text-blue-800 disabled:opacity-50"
              title="Réessayer"
            >
              🔄
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-1.5 py-1 text-base text-red-600 hover:text-red-800 disabled:opacity-50"
              title="Supprimer"
            >
              🗑
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex items-center gap-2">
      {renderActions()}
      {error && (
        <span className="text-xs text-red-600" title={error}>
          ⚠️
        </span>
      )}
    </div>
  );
}
